<script setup lang="ts">
/**
 * EN/RU language toggle, shared by the desktop sidebar and mobile bottom nav.
 * Assigning to `appLocale` switches translated text and, via
 * `config/date.ts`/`config/money.ts`, number/date formatting app-wide.
 */
import { useI18n } from 'vue-i18n'
import { appLocale, type AppLocale } from '@/i18n'

const { t } = useI18n()

function setLocale(value: AppLocale) {
  appLocale.value = value
}
</script>

<template>
  <div class="locale-switcher font-tufte-sans" role="group" :aria-label="t('locale.switchLabel')">
    <button
      type="button"
      class="locale-switcher__btn"
      :class="{ 'locale-switcher__btn--active': appLocale === 'en' }"
      :aria-pressed="appLocale === 'en'"
      @click="setLocale('en')"
    >
      {{ t('locale.en') }}
    </button>
    <button
      type="button"
      class="locale-switcher__btn"
      :class="{ 'locale-switcher__btn--active': appLocale === 'ru' }"
      :aria-pressed="appLocale === 'ru'"
      @click="setLocale('ru')"
    >
      {{ t('locale.ru') }}
    </button>
  </div>
</template>

<style scoped>
.locale-switcher {
  display: flex;
  gap: 0.25rem;
}

.locale-switcher__btn {
  appearance: none;
  cursor: pointer;
  border: 1px solid var(--color-tufte-border);
  background: transparent;
  color: var(--color-tufte-muted);
  border-radius: 5px;
  padding: 0.2rem 0.5rem;
  font-size: var(--text-tufte-xs);
  letter-spacing: 0.04em;
}

.locale-switcher__btn:hover {
  color: var(--color-tufte-text);
}

.locale-switcher__btn--active {
  color: var(--color-tufte-text);
  font-weight: 700;
  background: color-mix(in srgb, var(--color-tufte-border) 30%, transparent);
}
</style>
